import { Injectable } from '@angular/core';
import { QrCodeRecord, QrStyleId, QrUserAgentRoute, QrUserAgentRoutePreset } from '../app.types';

export interface QrRouteDraft {
  presetId: string;
  targetUrl: string;
  redirectPercentage: string;
  everyNthScan: string;
}

export interface QrFormState {
  title: string;
  targetUrl: string;
  userAgentRoutes: string;
  userAgentPreset: string;
  userAgentTargetUrl: string;
  userAgentRedirectPercentage: string;
  userAgentEveryNthScan: string;
  qrLabelText: string;
  qrIconSize: string;
  qrLabelSize: string;
  qrStyle: QrStyleId;
  categoryNames: string;
  trackAnalytics: boolean;
  providedByPage: boolean;
  skipResponseValidation: boolean;
}

interface QrFormStateOptions {
  authToken: boolean;
  skipProvidedByPage?: boolean;
}

interface RouteConfig {
  targetUrl: string;
  redirectPercentage?: number;
  everyNthScan?: number;
}

const DEFAULT_QR_ROUTE_DRAFT: QrRouteDraft = {
  presetId: '',
  targetUrl: '',
  redirectPercentage: '',
  everyNthScan: ''
};

@Injectable({ providedIn: 'root' })
export class QrFormStateService {
  readonly userAgentRoutePresets: QrUserAgentRoutePreset[] = [
    { id: 'ios', label: 'iOS app browsers', patterns: ['iPhone', 'iPad', 'iPod'] },
    { id: 'android', label: 'Android app browsers', patterns: ['Android'] },
    { id: 'mobile', label: 'Any mobile browser', patterns: ['Mobile', 'iPhone', 'Android'] },
    { id: 'tablet', label: 'Tablet browsers', patterns: ['iPad', 'Tablet'] },
    { id: 'desktop', label: 'Desktop browsers', patterns: ['Windows NT', 'Macintosh', 'X11'] },
    { id: 'windows', label: 'Windows desktop', patterns: ['Windows NT'] },
    { id: 'macos', label: 'macOS desktop', patterns: ['Macintosh'] },
    { id: 'facebook', label: 'Facebook in-app browser', patterns: ['FBAN', 'FBAV'] },
    { id: 'instagram', label: 'Instagram in-app browser', patterns: ['Instagram'] },
    { id: 'linkedin', label: 'LinkedIn in-app browser', patterns: ['LinkedInApp'] }
  ];

  state(options: QrFormStateOptions, overrides: Partial<QrFormState> = {}): QrFormState {
    return {
      title: '',
      targetUrl: '',
      userAgentRoutes: '',
      userAgentPreset: DEFAULT_QR_ROUTE_DRAFT.presetId,
      userAgentTargetUrl: DEFAULT_QR_ROUTE_DRAFT.targetUrl,
      userAgentRedirectPercentage: DEFAULT_QR_ROUTE_DRAFT.redirectPercentage,
      userAgentEveryNthScan: DEFAULT_QR_ROUTE_DRAFT.everyNthScan,
      qrLabelText: '',
      qrIconSize: '28',
      qrLabelSize: '100',
      qrStyle: 'classic',
      categoryNames: '',
      trackAnalytics: options.authToken,
      providedByPage: options.authToken && !options.skipProvidedByPage,
      skipResponseValidation: false,
      ...overrides
    };
  }

  stateFromRecord(record: QrCodeRecord, options: QrFormStateOptions): QrFormState {
    return this.state(options, {
      title: record.title || '',
      targetUrl: record.target_url,
      userAgentRoutes: this.routesToInput(record.user_agent_routes),
      qrLabelText: record.qr_label_text || '',
      qrIconSize: String(record.qr_icon_size),
      qrLabelSize: String(record.qr_label_size),
      qrStyle: record.qr_style,
      categoryNames: record.category_names.join(', '),
      trackAnalytics: record.track_analytics,
      providedByPage: record.provided_by_page,
      skipResponseValidation: false
    });
  }

  routesFromInput(value: string): QrUserAgentRoute[] {
    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line, index) => this.routeFromInputLine(line, index))
      .slice(0, 20);
  }

  routesToInput(routes: QrUserAgentRoute[] | null | undefined): string {
    return (routes || [])
      .map((route) => `${route.patterns.join(', ')} => ${route.target_url}${this.routeFrequencyInput(route)}`)
      .join('\n');
  }

  routeLabel(patterns: string[]): string {
    return this.presetForPatterns(patterns)?.label || patterns.join(', ');
  }

  routeFrequencyLabel(route: QrUserAgentRoute): string {
    if (route.every_nth_scan) {
      return ` every ${route.every_nth_scan} scans`;
    }

    return route.redirect_percentage && route.redirect_percentage < 100 ? ` ${route.redirect_percentage}% chance` : '';
  }

  routeLineFromDraft(formValue: {
    userAgentPreset: string;
    userAgentTargetUrl: string;
    userAgentEveryNthScan: string;
    userAgentRedirectPercentage: string;
  }): string | null | undefined {
    const targetUrl = formValue.userAgentTargetUrl.trim();
    const preset = this.presetForId(formValue.userAgentPreset);

    if (!preset || !targetUrl) {
      return undefined;
    }

    const frequencyInput = this.routeFrequencyInputFromDraft(formValue);
    if (frequencyInput === null) {
      return null;
    }

    return `${preset.patterns.join(', ')} => ${targetUrl}${frequencyInput}`;
  }

  routeFrequencyInputFromDraft(formValue: {
    userAgentEveryNthScan: string;
    userAgentRedirectPercentage: string;
  }): string | null {
    const everyNthScanValue = formValue.userAgentEveryNthScan.trim();
    const percentageValue = formValue.userAgentRedirectPercentage.trim();
    if (everyNthScanValue && percentageValue) {
      return null;
    }

    if (everyNthScanValue) {
      return ` every ${this.clampInteger(everyNthScanValue, 1, 10000)}`;
    }

    const redirectPercentage = percentageValue ? this.clampInteger(percentageValue, 1, 100) : 100;

    return redirectPercentage < 100 ? ` @ ${redirectPercentage}%` : '';
  }

  presetForId(presetId: string): QrUserAgentRoutePreset | undefined {
    return this.userAgentRoutePresets.find((candidate) => candidate.id === presetId);
  }

  routeMatchesPreset(line: string, preset: QrUserAgentRoutePreset): boolean {
    return line.toLowerCase().startsWith(`${preset.patterns.join(', ').toLowerCase()} =>`);
  }

  private routeFromInputLine(line: string, index: number): QrUserAgentRoute {
    const separatorIndex = line.indexOf('=>');
    if (separatorIndex < 0) {
      throw new Error(`User agent route ${index + 1} must use "matches => destination".`);
    }

    const patterns = line.slice(0, separatorIndex)
      .split(',')
      .map((pattern) => pattern.trim())
      .filter((pattern, patternIndex, patternsList) => pattern.length > 0 && patternsList.indexOf(pattern) === patternIndex);
    const routeConfig = this.parseRouteConfig(line.slice(separatorIndex + 2).trim());

    if (patterns.length === 0 || !routeConfig.targetUrl) {
      throw new Error(`User agent route ${index + 1} needs match text and a destination.`);
    }

    return {
      patterns: patterns.slice(0, 10),
      target_url: routeConfig.targetUrl,
      ...(routeConfig.redirectPercentage ? { redirect_percentage: routeConfig.redirectPercentage } : {}),
      ...(routeConfig.everyNthScan ? { every_nth_scan: routeConfig.everyNthScan } : {})
    };
  }

  private presetForPatterns(patterns: string[]): QrUserAgentRoutePreset | undefined {
    const normalizedPatterns = this.normalizedPatterns(patterns);

    return this.userAgentRoutePresets.find((candidate) => this.normalizedPatterns(candidate.patterns) === normalizedPatterns);
  }

  private normalizedPatterns(patterns: string[]): string {
    return patterns.map((pattern) => pattern.toLowerCase()).sort().join('|');
  }

  private parseRouteConfig(value: string): RouteConfig {
    let targetUrl = value;
    let redirectPercentage: number | undefined;
    let everyNthScan: number | undefined;

    const everyMatch = targetUrl.match(/\s+every\s+(\d+)\s*$/i);
    if (everyMatch) {
      everyNthScan = this.clampInteger(everyMatch[1], 1, 10000);
      targetUrl = targetUrl.slice(0, everyMatch.index).trim();
    }

    const percentageMatch = targetUrl.match(/\s+@\s*(\d{1,3})%?\s*$/);
    if (percentageMatch) {
      redirectPercentage = this.clampInteger(percentageMatch[1], 1, 100);
      targetUrl = targetUrl.slice(0, percentageMatch.index).trim();
    }

    if (everyNthScan && redirectPercentage) {
      throw new Error('User agent routes must use either redirect percentage or every nth scan, not both.');
    }

    return {
      targetUrl,
      redirectPercentage,
      ...(everyNthScan ? { everyNthScan } : {})
    };
  }

  private routeFrequencyInput(route: QrUserAgentRoute): string {
    if (route.every_nth_scan) {
      return ` every ${route.every_nth_scan}`;
    }

    return route.redirect_percentage && route.redirect_percentage < 100 ? ` @ ${route.redirect_percentage}%` : '';
  }

  private clampInteger(value: string, min: number, max: number): number {
    const parsedValue = Number.parseInt(value, 10);
    if (Number.isNaN(parsedValue)) {
      return min;
    }

    return Math.min(Math.max(parsedValue, min), max);
  }
}
